import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Application } from "@/contexts/ApplicationContext";
import { useActiveJob } from "@/contexts/ActiveJobContext";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { FileText, Info, User, Building2, Users, Ban } from "lucide-react";

interface TaxDetailsSectionProps {
  application: Application;
  setApplication: React.Dispatch<React.SetStateAction<Application>>;
}

const TaxDetailsSection: React.FC<TaxDetailsSectionProps> = ({
  application,
  setApplication,
}) => {
  const { job } = useActiveJob();
  // Resolve tax type from job stage/segment metadata
  const personalStage = job?.stages?.find((s) => s.id === "personal-details");
  const taxSegment = personalStage?.segments?.find(
    (seg) => (seg as unknown as { id?: string }).id === "tax-details"
  ) as unknown as
    | { id?: string; meta?: { taxTypes?: string[]; taxType?: string } }
    | undefined;
  // Normalize tax types to an array and filter to supported keys
  const TAX_TYPE_OPTIONS: Record<string, string> = {
    "sole-trader": "Sole Trader",
    "limited-company": "Limited Company",
    partnership: "Partnership",
    "not-applicable": "Not Applicable",
  };

  const resolvedTaxTypes: string[] = (
    taxSegment?.meta?.taxTypes ??
    (taxSegment?.meta?.taxType ? [taxSegment.meta.taxType] : undefined) ??
    []
  ).filter((t): t is string => typeof t === "string" && !!TAX_TYPE_OPTIONS[t]);

  // Only show options provided by resolvedTaxTypes
  const allowedTypes = resolvedTaxTypes;

  const selectedType = application.personalDetails.company.type || "";
  const clampedType = allowedTypes.includes(selectedType) ? selectedType : "";
  const handleNationalInsuranceChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 9);
    setApplication((prev) => ({
      ...prev,
      personalDetails: {
        ...prev.personalDetails,
        nationalInsuranceNumber: value,
      },
    }));
  };

  const handleUtrChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ""); // Remove non-digits
    if (value.length <= 10) {
      setApplication((prev) => ({
        ...prev,
        personalDetails: {
          ...prev.personalDetails,
          utrNumber: value,
        },
      }));
    }
  };

  const handleVatChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ""); // Remove non-digits
    if (value.length <= 9) {
      setApplication((prev) => ({
        ...prev,
        personalDetails: {
          ...prev.personalDetails,
          company: {
            ...prev.personalDetails.company,
            vatNumber: value,
          },
        },
      }));
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
        <FileText className="h-5 w-5 text-primary" />
        Tax Details
      </h2>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="companyType">Select the applicable type</Label>
          <Select
            value={clampedType || undefined}
            onValueChange={(value) =>
              setApplication((prev) => ({
                ...prev,
                personalDetails: {
                  ...prev.personalDetails,
                  company: {
                    ...prev.personalDetails.company,
                    type: value,
                  },
                },
              }))
            }
          >
            <SelectTrigger id="companyType">
              <SelectValue placeholder="Select company type" />
            </SelectTrigger>
            <SelectContent>
              {allowedTypes.map((t) => (
                <SelectItem key={t} value={t}>
                  <div className="flex items-center gap-2">
                    {t === "sole-trader" && (
                      <User className="h-4 w-4 text-muted-foreground" />
                    )}
                    {t === "limited-company" && (
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                    )}
                    {t === "partnership" && (
                      <Users className="h-4 w-4 text-muted-foreground" />
                    )}
                    {t === "not-applicable" && (
                      <Ban className="h-4 w-4 text-muted-foreground" />
                    )}
                    {TAX_TYPE_OPTIONS[t]}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="border-t mt-4 pt-4 flex items-center gap-2 text-sm text-muted-foreground">
          <Info className="h-4 w-4" />
          Please complete the following details
        </div>
        {/* Show data inputs ONLY after a selection is made */}
        {clampedType && (
          <>
            <div className="space-y-2">
              <Label htmlFor="nationalInsuranceNumber">
                National Insurance Number
              </Label>
              <Input
                id="nationalInsuranceNumber"
                value={application.personalDetails.nationalInsuranceNumber}
                onChange={handleNationalInsuranceChange}
                placeholder="Enter your National Insurance Number"
                minLength={9}
                maxLength={9}
                pattern="[A-Z]{2}[0-9]{6}[A-Z]"
                title="National Insurance Number must be 2 letters, 6 numbers, and 1 letter (e.g., AB123456C)"
              />
              <p className="text-sm text-gray-500">
                National Insurance Number must be 2 letters, 6 numbers, and 1 letter (e.g.
                AB123456C)
              </p>
            </div>

            {/* UTR for Sole Trader, Limited Company, and Partnership */}
            {clampedType !== "not-applicable" && (
              <div className="space-y-2">
                <Label htmlFor="utrNumber">
                  Unique Taxpayer Reference (UTR)
                </Label>
                <Input
                  id="utrNumber"
                  value={application.personalDetails.utrNumber}
                  onChange={handleUtrChange}
                  placeholder="Enter your UTR Number"
                  minLength={10}
                  maxLength={10}
                  pattern="[0-9]{10}"
                  title="UTR Number must be exactly 10 digits"
                />
                <p className="text-sm text-gray-500">
                  UTR Number should be 10 digits
                </p>
              </div>
            )}

            {/* Company Number for Limited Company and Partnership */}
            {(clampedType === "limited-company" ||
              clampedType === "partnership") && (
              <div className="space-y-2">
                <Label htmlFor="companyName">Company Name</Label>
                <Input
                  id="companyName"
                  value={application.personalDetails.company.number}
                  onChange={(e) =>
                    setApplication((prev) => ({
                      ...prev,
                      personalDetails: {
                        ...prev.personalDetails,
                        company: {
                          ...prev.personalDetails.company,
                          number: e.target.value,
                        },
                      },
                    }))
                  }
                  placeholder="Enter your company name"
                  required
                />
              </div>
            )}

            {/* VAT Number for Limited Company and Partnership */}
            {(clampedType === "limited-company" ||
              clampedType === "partnership") && (
              <div className="space-y-2">
                <Label htmlFor="vatNumber">VAT Number (optional)</Label>
                <Input
                  id="vatNumber"
                  value={application.personalDetails.company.vatNumber}
                  onChange={handleVatChange}
                  placeholder="Enter your VAT number"
                  maxLength={9}
                />
                <p className="text-sm text-gray-500">
                  VAT Number should be 9 digits
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TaxDetailsSection;
